/**
 * The shapes the analytics screens read.
 *
 * Declared here rather than imported from the API: ADR 0001 settles that types
 * are duplicated between the API and its clients rather than shared through a
 * workspace. `FunnelResponse` is served — `GET /v1/admin/analytics/funnel` returns it,
 * so its shape has to keep matching the API's. The rest is not: Drop-off,
 * Signals and Journeys still read hand-written fixtures from
 * `./fixtures/analytics`, and wiring one means swapping that import for an
 * `api.get` call, not rewriting the components.
 *
 * The funnel itself is defined in docs/analytics.md.
 */

export const FUNNEL_STEP_IDS = [
  'first-run',
  'assessment',
  'registration',
  'subscription',
  'payment',
] as const;

export type FunnelStepId = (typeof FUNNEL_STEP_IDS)[number];

/**
 * Which clock a step is measured on.
 *
 * Steps 1–4 run from the user's first run. Step 5 cannot: with a seven-day
 * trial nobody reaches it inside the first week, so on a first-run clock the
 * column reads zero and looks like an outage.
 */
export type FunnelClock = 'first-run' | 'trial';

export interface FunnelStep {
  id: FunnelStepId;
  label: string;
  /** One line, shown under the label. Kept short enough to sit in a bar row. */
  description: string;
  clock: FunnelClock;
}

export const FUNNEL_STEPS: FunnelStep[] = [
  {
    id: 'first-run',
    label: 'First run',
    description: 'App opened, first server call. Includes cold-open bounces.',
    clock: 'first-run',
  },
  {
    id: 'assessment',
    label: 'Assessment complete',
    description: 'Act II reached its ending.',
    clock: 'first-run',
  },
  {
    id: 'registration',
    label: 'Registration',
    description: 'An account exists. Signing back in is not one.',
    clock: 'first-run',
  },
  {
    id: 'subscription',
    label: 'Subscription started',
    description: 'Trial begun with payment details given.',
    clock: 'first-run',
  },
  {
    id: 'payment',
    label: 'First payment taken',
    description: 'Trial lapsed into paid.',
    clock: 'trial',
  },
];

export type OffsetKey = 'h24' | 'd7' | 'd28';

export interface Offset {
  key: OffsetKey;
  label: string;
  /** Spelled out where the short label would be ambiguous. */
  description: string;
}

export const OFFSETS: Offset[] = [
  { key: 'h24', label: '24h', description: '24 hours after first run' },
  { key: 'd7', label: '7d', description: '7 days after first run' },
  { key: 'd28', label: '28d', description: '28 days after first run' },
];

/**
 * A count at one offset.
 *
 * `null` means the cohort is not old enough for the offset to mean anything —
 * rendered as an em dash, never as zero. A zero here would be a real,
 * measured zero.
 */
export type OffsetCounts = Record<OffsetKey, number | null>;

/**
 * Whether a rung has a data model behind it.
 *
 * `not-built` is not the same as a count of zero and must never render as one:
 * zero is a measurement, and "nothing records this yet" is not. The API sends
 * this rather than letting the client infer it from a missing number.
 */
export type RungStatus = 'measured' | 'not-built';

export interface FunnelRung {
  step: FunnelStepId;
  status: RungStatus;
  /** Null whenever status is not `measured`. */
  counts: OffsetCounts | null;
  /**
   * Present on `registration` only, and sums to `counts`.
   *
   * Somebody choosing an account and somebody required to have one because they
   * were subscribing are two different decisions, and docs/analytics.md is
   * explicit that merging them inflates the next conversion. Nothing requires an
   * account yet, so `required` is a measured zero rather than an absence.
   *
   * A split and not a rung: the two do not contain one another, so it belongs in
   * the table and never in the funnel shape.
   */
  breakdown?: {
    chose: OffsetCounts;
    required: OffsetCounts;
  };
}

/**
 * One first-run cohort, as GET /v1/admin/analytics/funnel returns it.
 *
 * Declared here rather than imported from the API: ADR 0001 settles that types
 * are duplicated between the API and its clients rather than shared through a
 * workspace.
 */
export interface CohortFunnel {
  /** ISO week the cohort's first runs fell in, e.g. `2026-W36`. */
  id: string;
  label: string;
  startedOn: string;
  /** The week has not closed, so this cohort is still accumulating. */
  inProgress: boolean;
  rungs: FunnelRung[];
}

export interface FunnelResponse {
  cohorts: CohortFunnel[];
  generatedAt: string;
}

export interface BounceBucket {
  /** Turn index the user stopped at. */
  turn: number;
  users: number;
}

export interface DropOff {
  /** The window these aggregates cover, for the caption. */
  window: string;
  assessment: {
    reached: number;
    completed: number;
    bounced: number;
    buckets: BounceBucket[];
  };
  registration: {
    inFirstSession: number;
    later: number;
    chose: number;
    requiredToSubscribe: number;
  };
  trial: {
    started: number;
    converted: number;
    cancelledVoluntary: number;
    failedInvoluntary: number;
  };
}

export interface SurfaceCoverage {
  surface: string;
  /** Opened it. */
  touched: number;
  /** Cleared the quality bar for that surface. */
  counted: number;
  /** What "counted" means here — the bar is different per surface. */
  bar: string;
}

export interface LevelMove {
  from: string;
  to: string;
  users: number;
  medianDays: number;
}

export interface Signals {
  window: string;
  firstRuns: number;
  secondSessions: number;
  /** Store-sourced, and deliberately not a funnel step. */
  installs: {
    total: number;
    source: string;
    caveat: string;
  };
  coverage: SurfaceCoverage[];
  levelMoves: LevelMove[];
}

/**
 * Which side of the account seam an event falls on.
 *
 * `adoption` is the single event where a device-scoped anonymous user is
 * taken over by an account. It gets its own phase because it is the hardest
 * thing in the data model and the timeline is where it becomes obvious
 * whether the stitching reads correctly.
 */
export type JourneyPhase = 'anonymous' | 'adoption' | 'account';

export interface JourneyEvent {
  id: string;
  at: string;
  title: string;
  detail?: string;
  phase: JourneyPhase;
  /** Set when this event is what advanced a funnel rung. */
  milestone?: FunnelStepId;
}

export interface Journey {
  id: string;
  /** Truncated for display; the full value would be a device-scoped id. */
  anonymousId: string;
  label: string;
  language: string;
  platform: 'iOS' | 'Android';
  firstRunAt: string;
  /** The furthest rung this user has reached. */
  reached: FunnelStepId;
  /** One line on what makes this journey worth reading. */
  headline: string;
  events: JourneyEvent[];
}
